import React, { useState } from "react";
import {
    Box,
    Button,
    Paper,
    Stack,
    Typography,
    Alert,
    LinearProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import * as XLSX from "xlsx";
import { getCookie } from "../../../helpers/cookies";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const PAYMENT_LIST_URL = `${API_BASE_URL}/payment/payment-list`;
const BOOKING_LIST_URL = `${API_BASE_URL}/bookings/list`;
const BOOKING_SINGLE_URL = `${API_BASE_URL}/bookings`;
const ACTIVITY_LIST_URL = `${API_BASE_URL}/activity/list`;

const DashboardContainer = () => {
    const [file, setFile] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState({
        type: "",
        message: "",
    });
    const [resultInfo, setResultInfo] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];

        setStatus({
            type: "",
            message: "",
        });

        setResultInfo(null);

        if (!selectedFile) {
            setFile(null);
            return;
        }

        const extension = selectedFile.name
            .split(".")
            .pop()
            ?.toLowerCase();

        if (!["xlsx", "xls"].includes(extension)) {
            setFile(null);

            setStatus({
                type: "error",
                message: "Please select an Excel file (.xlsx or .xls).",
            });

            return;
        }

        setFile(selectedFile);
    };

    const handleConvert = async () => {
        if (!file) {
            setStatus({
                type: "error",
                message: "Please select an Excel file first.",
            });

            return;
        }

        setIsConverting(true);
        setProgress(0);

        setStatus({
            type: "info",
            message: "Reading Excel file...",
        });

        setResultInfo(null);

        try {
            const inputRows = await readExcelRows(file);

            const transactionColumn = findTransactionColumn(inputRows);

            if (!transactionColumn) {
                throw new Error(
                    'Could not find the "CCAvenue Ref Number" column in the uploaded Excel.'
                );
            }

            const transactions = inputRows
                .map((row) =>
                    normalizeTransactionId(row[transactionColumn])
                )
                .filter(Boolean);

            if (!transactions.length) {
                throw new Error(
                    "No CCAvenue Ref Number values were found in the Excel."
                );
            }

            const uniqueTransactions = [...new Set(transactions)];

            setStatus({
                type: "info",
                message: `Found ${uniqueTransactions.length} unique transaction(s). Fetching details...`,
            });

            const reportData = await buildReport(
                uniqueTransactions,
                (completed, total, message) => {
                    setProgress(
                        Math.round((completed / total) * 100)
                    );

                    setStatus({
                        type: "info",
                        message,
                    });
                }
            );

            const workbook = createWorkbook(
                reportData.rows,
                reportData.activityColumns
            );

            const fileName =
                `CCAvenue_Activity_Report_${formatDateForFile(
                    new Date()
                )}.xlsx`;

            XLSX.writeFile(workbook, fileName);

            setProgress(100);

            setResultInfo({
                transactions: uniqueTransactions.length,
                rows: reportData.rows.length,
                activities: reportData.activityColumns.length,
                fileName,
            });

            setStatus({
                type: "success",
                message: `Report generated successfully: ${fileName}`,
            });
        } catch (error) {
            console.error(
                "Excel report generation error:",
                error
            );

            setStatus({
                type: "error",
                message:
                    error?.message ||
                    "Something went wrong while generating the report.",
            });
        } finally {
            setIsConverting(false);
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                p: 3,
                pt: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: "100%",
                    p: 4,
                    borderRadius: 3,
                }}
            >
                <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                >
                    CCAvenue Activity Report
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    Upload the CCAvenue Excel file and generate
                    the activity payment report.
                </Typography>

                <Stack
                    direction={{
                        xs: "column",
                        md: "row",
                    }}
                    spacing={2}
                    alignItems={{
                        xs: "stretch",
                        md: "center",
                    }}
                >
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        disabled={isConverting}
                        sx={{
                            minWidth: 210,
                        }}
                    >
                        Choose Excel File

                        <input
                            hidden
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                        />
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        onClick={handleConvert}
                        disabled={!file || isConverting}
                        sx={{
                            minWidth: 150,
                        }}
                    >
                        {isConverting
                            ? "Converting..."
                            : "Convert Excel"}
                    </Button>
                </Stack>

                {file && (
                    <Typography sx={{ mt: 2 }}>
                        Selected file:{" "}
                        <b>{file.name}</b>
                    </Typography>
                )}

                {isConverting && (
                    <Box sx={{ mt: 3 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                        />

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                mt: 1,
                                display: "block",
                            }}
                        >
                            {progress}%
                        </Typography>
                    </Box>
                )}

                {status.message && (
                    <Alert
                        severity={
                            status.type || "info"
                        }
                        sx={{ mt: 3 }}
                    >
                        {status.message}
                    </Alert>
                )}

                {resultInfo && (
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: "#f7f9fc",
                        }}
                    >
                        <Typography variant="body2">
                            Transactions:{" "}
                            <b>
                                {resultInfo.transactions}
                            </b>
                        </Typography>

                        <Typography variant="body2">
                            Output rows:{" "}
                            <b>
                                {resultInfo.rows}
                            </b>
                        </Typography>

                        <Typography variant="body2">
                            Activities:{" "}
                            <b>
                                {resultInfo.activities}
                            </b>
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default DashboardContainer;

/* ========================================================= */
/* Excel Reader                                               */
/* ========================================================= */

const readExcelRows = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const buffer = new Uint8Array(
                    event.target.result
                );

                const workbook = XLSX.read(buffer, {
                    type: "array",
                });

                const firstSheetName =
                    workbook.SheetNames[0];

                const worksheet =
                    workbook.Sheets[firstSheetName];

                if (!worksheet) {
                    throw new Error(
                        "The Excel file does not contain a readable sheet."
                    );
                }

                const rows =
                    XLSX.utils.sheet_to_json(
                        worksheet,
                        {
                            defval: "",
                            raw: true,
                        }
                    );

                resolve(rows);
            } catch (error) {
                reject(
                    new Error(
                        `Unable to read the Excel file: ${error.message}`
                    )
                );
            }
        };

        reader.onerror = () =>
            reject(
                new Error(
                    "Unable to read the uploaded Excel file."
                )
            );

        reader.readAsArrayBuffer(file);
    });

/* ========================================================= */
/* Excel Column Helpers                                       */
/* ========================================================= */

const findTransactionColumn = (rows) => {
    if (!rows.length) return null;

    const possibleNames = [
        "CCAvenue Ref Number",
        "CCAvenue Ref No",
        "CCAvenue Reference Number",
        "CCAVENUE REF NUMBER",
        "Transaction Id",
        "Transaction ID",
    ];

    const headers = Object.keys(rows[0]);

    const normalize = (value) =>
        String(value || "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");

    const headerMap = new Map(
        headers.map((header) => [
            normalize(header),
            header,
        ])
    );

    for (const name of possibleNames) {
        const found =
            headerMap.get(normalize(name));

        if (found) {
            return found;
        }
    }

    return (
        headers.find((header) => {
            const key = normalize(header);

            return (
                key.includes("ccavenue") &&
                (key.includes("ref") ||
                    key.includes("transaction"))
            );
        }) || null
    );
};

const normalizeTransactionId = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "";
    }

    return String(value).trim();
};

/* ========================================================= */
/* API Helpers                                                */
/* ========================================================= */

const fetchJson = async (url) => {
    // Your ASMC application stores JWT in the "asmc_token" cookie
    const token = getCookie("asmc_token");

    if (!token) {
        throw new Error(
            "Authentication token not found. Please log in again."
        );
    }

    const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    let payload = null;

    try {
        payload = await response.json();
    } catch {
        throw new Error(
            `API returned invalid JSON: ${url}`
        );
    }

    if (!response.ok) {
        throw new Error(
            payload?.message ||
                payload?.error ||
                `API request failed with status ${response.status}`
        );
    }

    if (payload?.success === false) {
        throw new Error(
            payload?.message ||
                "API request failed."
        );
    }

    return payload;
};

const getArrayFromApiResponse = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    const candidates = [
        // Current API response structure
        payload?.result?.result,

        // Other possible response structures
        payload?.result,
        payload?.result?.data,
        payload?.result?.records,
        payload?.result?.docs,
        payload?.result?.list,
        payload?.result?.items,
        payload?.result?.rows,

        payload?.data,
        payload?.data?.records,
        payload?.data?.docs,
        payload?.data?.list,
        payload?.data?.items,
        payload?.data?.rows,
    ];

    for (const item of candidates) {
        if (Array.isArray(item)) {
            return item;
        }
    }

    return [];
};

const getPaymentByTrackingId = async (trackingId) => {
    const params = new URLSearchParams({
        pageNo: "0",
        limit: "10",
        sortBy: "-1",
        sortField: "createdAt",
        keywords: trackingId,
        filter_by: "any_word",
        payment_status: "Success",
    });

    const payload = await fetchJson(
        `${PAYMENT_LIST_URL}?${params.toString()}`
    );

    const records =
        getArrayFromApiResponse(payload);

    for (const payment of records) {
        let paymentResponse = null;

        try {
            paymentResponse =
                payment?.payment_response
                    ? JSON.parse(
                          payment.payment_response
                      )
                    : null;
        } catch {
            paymentResponse = null;
        }

        const ids = [
            payment?.payment_id,
            paymentResponse?.tracking_id,
            paymentResponse?.order_id,
        ]
            .filter(Boolean)
            .map((value) =>
                normalizeTransactionId(value)
            );

        if (ids.includes(trackingId)) {
            return payment;
        }
    }

    return records.length === 1
        ? records[0]
        : null;
};

const getBookingFromList = async (
    bookingId,
    preferredType = "enrollment"
) => {
    const types = [
        ...new Set([
            preferredType,
            "enrollment",
            "booking",
        ]),
    ];

    for (const type of types) {
        const params = new URLSearchParams({
            pageNo: "0",
            limit: "1",
            sortBy: "-1",
            sortField: "createdAt",
            booking_id: String(bookingId),
            type,
        });

        const payload = await fetchJson(
            `${BOOKING_LIST_URL}?${params.toString()}`
        );

        const records =
            getArrayFromApiResponse(payload);

        if (records.length > 0) {
            return records[0];
        }
    }

    return null;
};

const getBookingByMongoId = async (mongoId) => {
    const params = new URLSearchParams({
        _id: String(mongoId),
    });

    const payload = await fetchJson(
        `${BOOKING_SINGLE_URL}?${params.toString()}`
    );

    return (
        getArrayFromApiResponse(payload)[0] ||
        null
    );
};

const getActivityById = async (
    activityId,
    cache
) => {
    if (!activityId) {
        return null;
    }

    const cacheKey = String(activityId);

    if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
    }

    const params = new URLSearchParams({
        activity_id: String(activityId),
    });

    const payload = await fetchJson(
        `${ACTIVITY_LIST_URL}?${params.toString()}`
    );

    const activity =
        getArrayFromApiResponse(payload)[0] ||
        null;

    cache.set(cacheKey, activity);

    return activity;
};

/* ========================================================= */
/* Payment → Booking                                          */
/* ========================================================= */

const resolvePaymentBookings = async (payment) => {
    // The payment API already returns populated bookings_data.
    if (Array.isArray(payment?.bookings_data)) {
        return payment.bookings_data.filter(Boolean);
    }

    // Fallback only when bookings_data is not available.
    const bookingIds = Array.isArray(payment?.booking_id)
        ? payment.booking_id
        : payment?.booking_id
            ? [payment.booking_id]
            : [];

    const bookings = [];

    for (const bookingId of bookingIds) {
        const rawId =
            typeof bookingId === "object" && bookingId !== null
                ? bookingId?._id || bookingId?.booking_id
                : bookingId;

        if (!rawId) continue;

        let booking = null;

        // Mongo ObjectId
        if (
            typeof rawId === "string" &&
            /^[a-fA-F0-9]{24}$/.test(rawId)
        ) {
            booking = await getBookingByMongoId(rawId);
        }

        // Numeric booking ID
        if (!booking && /^\d+$/.test(String(rawId))) {
            booking = await getBookingFromList(
                rawId,
                payment?.booking_type === "booking"
                    ? "booking"
                    : "enrollment"
            );
        }

        if (booking) {
            bookings.push(booking);
        }
    }

    return bookings;
};

/* ========================================================= */
/* Participant Logic                                          */
/* ========================================================= */

const getMemberName = (member) => {
    if (!member) return "";

    return (
        member?.name ||
        member?.full_name ||
        member?.member_name ||
        ""
    );
};

const extractParticipants = (
    booking,
    payment,
    primaryMemberName
) => {
    const participants = [];

    /*
     * 1. Hourly/player based booking
     */
    if (
        Array.isArray(booking?.players) &&
        booking.players.length > 0
    ) {
        booking.players.forEach((player) => {
            const name =
                player?.name ||
                player?.full_name ||
                player?.player_name ||
                "";

            if (!name) return;

            const normalizedName = normalizeName(name);
            const normalizedPrimary = normalizeName(primaryMemberName);

            participants.push({
                name,
                isPrimary:
                    player?.is_primary === true ||
                    player?.primary === true ||
                    normalizedName === normalizedPrimary,
                isMember:
                    player?.is_member === true ||
                    player?.is_member === "Yes",
                raw: player,
            });
        });
    }

    /*
     * 2. Enrollment/family member booking
     *
     * This is the structure present in your API response.
     */
    if (
        participants.length === 0 &&
        Array.isArray(booking?.family_member)
    ) {
        booking.family_member
            .filter(Boolean)
            .forEach((familyMember) => {
                const name =
                    familyMember?.name ||
                    familyMember?.full_name ||
                    familyMember?.member_name ||
                    "";

                if (!name) return;

                participants.push({
                    name,
                    isPrimary: false,
                    isMember: true,
                    raw: familyMember,
                });
            });
    }

    /*
     * 3. Only use primary member when this booking
     *    itself has no participant information.
     *
     *    This prevents SHREENIVAS from incorrectly appearing
     *    for every secondary-member booking.
     */
    if (participants.length === 0) {
        const bookingPrimaryName =
            getMemberName(booking?.member_id) ||
            booking?.member_data?.[0]?.name ||
            booking?.member_data?.name ||
            primaryMemberName ||
            "";

        if (bookingPrimaryName) {
            participants.push({
                name: bookingPrimaryName,
                isPrimary: true,
                isMember: true,
                raw: booking,
            });
        }
    }

    return participants.filter(
        (participant) =>
            participant.name &&
            String(participant.name).trim()
    );
};

/* ========================================================= */
/* Amount Logic                                                */
/* ========================================================= */

const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : 0;
};

const resolveParticipantAmount = (participant, booking, participants) => {
    const raw = participant?.raw || {};

    /*
     * For hourly/player bookings, use the amount explicitly
     * stored against the player.
     */
    const playerAmount =
        raw?.amount ??
        raw?.price ??
        raw?.fee ??
        raw?.fees ??
        raw?.paid_amount ??
        raw?.amount_paid ??
        raw?.final_amount;

    if (
        Array.isArray(booking?.players) &&
        booking.players.length > 0 &&
        playerAmount !== undefined &&
        playerAmount !== null &&
        playerAmount !== ""
    ) {
        return toNumber(playerAmount);
    }

    /*
     * For enrollment/family-member bookings, the authoritative
     * activity amount is the booking's total_amount.
     *
     * Do NOT use familyMember.plans.final_amount here because
     * that belongs to the membership plan, not necessarily
     * to the activity enrollment.
     */
    if (
        Array.isArray(booking?.family_member) &&
        booking.family_member.length > 0
    ) {
        if (participants.length === 1) {
            return toNumber(booking?.total_amount);
        }

        /*
         * If several participants share one booking and no
         * participant-specific amount exists, use the booking
         * amount only once to avoid double counting.
         */
        return participants[0] === participant
            ? toNumber(booking?.total_amount)
            : 0;
    }

    /*
     * Normal single-participant booking.
     */
    if (participants.length === 1) {
        return toNumber(booking?.total_amount);
    }

    return 0;
};

/* ========================================================= */
/* Report Builder                                             */
/* ========================================================= */

const buildReport = async (
    transactionIds,
    onProgress
) => {
    const activityCache = new Map();

    const rows = [];
    const activityColumns = [];
    const activitySet = new Set();

    for (
        let index = 0;
        index < transactionIds.length;
        index++
    ) {
        const transactionId =
            transactionIds[index];

        onProgress(
            index,
            transactionIds.length,
            `Processing transaction ${
                index + 1
            } of ${
                transactionIds.length
            }: ${transactionId}`
        );

        const payment =
            await getPaymentByTrackingId(
                transactionId
            );

        if (!payment) {
            rows.push({
                "Transaction Id":
                    transactionId,

                "Primary member Name": "",

                "Secondary Members":
                    "Payment not found",

                "Total amount Paid": "",
            });

            continue;
        }

        const primaryMemberName =
            payment?.member_data?.[0]?.name ||
            payment?.memberData?.name ||
            payment?.name ||
            "";

        const bookings =
            await resolvePaymentBookings(
                payment
            );

        const participantRows = [];

        for (const booking of bookings) {
            const bookingActivityId =
                booking?.activity_id?._id ||
                booking?.activity_id;

            let activity = null;

            if (Array.isArray(payment?.activity_data)) {
                activity =
                    payment.activity_data.find(
                        (item) =>
                            String(item?._id) ===
                                String(bookingActivityId) ||
                            String(item?.activity_id) ===
                                String(bookingActivityId)
                    ) || null;
            }

            if (!activity?.name && booking?.activity_id?.name) {
                activity = booking.activity_id;
            }

            if (!activity?.name && bookingActivityId) {
                activity = await getActivityById(
                    bookingActivityId,
                    activityCache
                );
            }

            const activityName =
                activity?.name ||
                booking?.activity_name ||
                "Unknown Activity";

            if (!activitySet.has(activityName)) {
                activitySet.add(
                    activityName
                );

                activityColumns.push(
                    activityName
                );
            }

            const participants =
                extractParticipants(
                    booking,
                    payment,
                    primaryMemberName
                );

            participants.forEach(
                (participant) => {
                    const amount =
                        resolveParticipantAmount(
                            participant,
                            booking,
                            participants
                        );

                    participantRows.push({
                        name:
                            participant.name,

                        activityName,

                        amount,
                    });
                }
            );
        }

        /*
         * If there are no bookings, still create
         * a transaction row.
         */
        if (!participantRows.length) {
            participantRows.push({
                name:
                    primaryMemberName,

                activityName:
                    "",

                amount: 0,
            });
        }

        const totalAmountPaid =
            toNumber(
                payment?.amount_paid
            );

        /*
         * Create one row per player,
         * but one transaction group.
         */
        participantRows.forEach(
            (participant, rowIndex) => {
                const row = {
                    "Transaction Id":
                        rowIndex === 0
                            ? transactionId
                            : "",

                    "Primary member Name":
                        rowIndex === 0
                            ? primaryMemberName
                            : "",

                    "Secondary Members":
                        participant.name,
                };

                activityColumns.forEach(
                    (activity) => {
                        row[activity] =
                            activity ===
                            participant.activityName &&
                            participant.amount > 0
                                ? participant.amount
                                : "-";
                    }
                );

                row[
                    "Total amount Paid"
                ] =
                    rowIndex === 0
                        ? totalAmountPaid
                        : "";

                rows.push(row);
            }
        );
    }

    onProgress(
        transactionIds.length,
        transactionIds.length,
        "Preparing Excel file..."
    );

    return {
        rows,
        activityColumns,
    };
};

/* ========================================================= */
/* Create Workbook                                             */
/* ========================================================= */

const createWorkbook = (
    rows,
    activityColumns
) => {
    const headers = [
        "Transaction Id",
        "Primary member Name",
        "Secondary Members",
        ...activityColumns,
        "Total amount Paid",
    ];

    const worksheetData = [
        headers,
        ...rows.map((row) =>
            headers.map(
                (header) =>
                    row[header] ?? ""
            )
        ),
    ];

    const worksheet =
        XLSX.utils.aoa_to_sheet(
            worksheetData
        );

    worksheet["!cols"] = [
        { wch: 20 },
        { wch: 28 },
        { wch: 30 },
        ...activityColumns.map(() => ({
            wch: 15,
        })),
        { wch: 18 },
    ];

    const workbook =
        XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Activity Report"
    );

    return workbook;
};

/* ========================================================= */
/* Date Helper                                                 */
/* ========================================================= */

const normalizeName = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

const formatDateForFile = (date) => {
    const year =
        date.getFullYear();

    const month = String(
        date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
        date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
};